import React, { useEffect, useRef, useMemo, useState } from 'react'
import ApexCharts from 'apexcharts'
import './LineChart.css'

const DEFAULT_LEGEND_ITEM_MARGIN = { horizontal: 5, vertical: 0 }
const DEFAULT_GRID_PADDING = { bottom: 30, left: 20, right: 10 }

interface LineChartSeries {
    name: string
    data: (number | null)[]
}

interface LineChartProps {
    categories: string[]
    series: LineChartSeries[]
    customTooltip?: (context: {
        seriesIndex: number
        dataPointIndex: number
        value: number | null
        seriesName: string
        category: string
    }) => string
    goalLine?: number
    goalLabel?: string
    goalLabelOffsetX?: number
    height?: number
    yAxisLabel?: string
    xAxisLabel?: string
    colors?: string[]
    fontFamily?: string
    showToolbar?: boolean
    legendPosition?: 'top' | 'right' | 'bottom' | 'left'
    legendAlign?: 'left' | 'center' | 'right'
    showLegend?: boolean
    exportFilename?: string
    yAxisTitleStyle?: React.CSSProperties
    xAxisTitleStyle?: React.CSSProperties
    annotations?: any
    markers?: boolean
    showXAxisTooltip?: boolean
    decimals?: number
    noDataMessage?: string
    min?: number
    max?: number
    tickAmount?: number
    isLoading?: boolean
    showZeroForNull?: boolean
    treatNullAsZero?: boolean
    showZeroLine?: boolean
    yAxisDecimals?: number
    xAxisLabelFontSize?: string
    yAxisLabelFontSize?: string
    showDataLabels?: boolean
    dataLabelSuffix?: string
    dataLabelsOffsetY?: number
    dataLabelsOffsetYPerSeries?: number[]
    dataLabelsEnabledPerSeries?: boolean[]
    curve?: 'smooth' | 'straight' | 'stepline'
    legendOffsetY?: number
    legendItemMargin?: {
        horizontal?: number
        vertical?: number
    }
    showHorizontalGridLines?: boolean
    gridPadding?: {
        top?: number
        right?: number
        bottom?: number
        left?: number
    }
    useCustomLegend?: boolean
    legendFontSize?: string
    legendFontWeight?: string | number
    legendGap?: string
    legendMarkerSize?: string
    showInfoIcon?: boolean
    chartTitle?: string
    chartSubtitle?: string
    pageTitle?: string
    includeLegendInExport?: boolean
}

interface CustomLegendProps {
    showLegend: boolean
    series: LineChartSeries[]
    colors: string[]
    legendGap: string
    legendFontSize: string
    legendFontWeight: string | number
    legendMarkerSize: string
    legendOffsetY: number
    fontFamily: string
    height: number
    chartInstance: React.MutableRefObject<ApexCharts | null>
    showInfoIcon?: boolean
    layout?: 'horizontal' | 'vertical'
    horizontalAlign?: 'left' | 'center' | 'right'
}

const CustomLegend: React.FC<CustomLegendProps> = ({
    showLegend,
    series,
    colors,
    legendGap,
    legendFontSize,
    legendFontWeight,
    legendMarkerSize,
    legendOffsetY,
    fontFamily,
    height,
    chartInstance,
    showInfoIcon = false,
    layout = 'vertical',
    horizontalAlign = 'center',
}) => {
    if (!showLegend) return null

    const [hiddenSeries, setHiddenSeries] = React.useState<string[]>([])

    const handleLegendClick = (seriesName: string) => {
        if (chartInstance.current) {
            chartInstance.current.toggleSeries(seriesName)

            setHiddenSeries(prev => {
                if (prev.includes(seriesName)) {
                    return prev.filter(s => s !== seriesName)
                } else {
                    return [...prev, seriesName]
                }
            })
        }
    }

    const isHorizontal = layout === 'horizontal'

    return (
        <div className="custom-legend" style={{
            display: 'flex',
            flexDirection: isHorizontal ? 'row' : 'column',
            justifyContent: isHorizontal ? horizontalAlign === 'left' ? 'flex-start' : horizontalAlign === 'right' ? 'flex-end' : 'center' : 'center',
            alignItems: isHorizontal ? 'center' : 'flex-start',
            gap: legendGap,
            padding: isHorizontal ? '4px 0' : '0 0 0 10px',
            height: isHorizontal ? 'auto' : '100%',
            maxHeight: isHorizontal ? undefined : height,
            transform: legendOffsetY ? `translateY(${legendOffsetY}px)` : undefined,
            flexWrap: 'wrap',
        }}>
            {series.map((s, index) => {
                const isHidden = hiddenSeries.includes(s.name)
                return (
                    <div
                        key={index}
                        onClick={() => handleLegendClick(s.name)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: legendFontSize,
                            fontWeight: legendFontWeight,
                            fontFamily: fontFamily,
                            color: '#373D3F',
                            lineHeight: '1.2',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s',
                            padding: '2px 0',
                            opacity: isHidden ? 0.5 : 1
                        }}
                        className="custom-legend-item"
                    >
                        <span style={{
                            width: legendMarkerSize,
                            height: legendMarkerSize,
                            borderRadius: '50%',
                            backgroundColor: colors[index % colors.length],
                            display: 'inline-block',
                            flexShrink: 0,
                            opacity: isHidden ? 0.5 : 1,
                            filter: isHidden ? 'grayscale(100%)' : 'none'
                        }}></span>
                        <span style={{ color: '#373d3f', whiteSpace: 'nowrap' }}>{s.name}</span>
                    </div>
                )
            })}
                    {showInfoIcon && (
            <div className="legend-info-wrapper">
                <span className="legend-info-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#808080"/>
                        <text
                            x="12"
                            y="17"
                            fontFamily="Arial, sans-serif"
                            fontSize="14"
                            fontWeight="bold"
                            fill="white"
                            textAnchor="middle"
                        >
                            i
                        </text>
                    </svg>
                </span>

                <div className="legend-tooltip">
                    Click on a legend item to hide or show that data series on the chart
                </div>
            </div>
        )}
        </div>
    )
}

export interface LineChartHandle {
    exportToCSV: () => void;
    exportToSVG: () => void;
    exportToPNG: () => void;
}

const LineChart = React.forwardRef<LineChartHandle, LineChartProps>(({
    categories,
    series,
    customTooltip,
    goalLine,
    goalLabel = 'Goal',
    goalLabelOffsetX = 0,
    height = 450,
    yAxisLabel = '',
    xAxisLabel = 'Time Period',
    colors = ['#0072B2'],
    fontFamily = 'Helvetica, Arial, sans-serif',
    showToolbar = true,
    legendPosition = 'top',
    legendAlign = 'right',
    showLegend = true,
    exportFilename,
    annotations,
    markers = true,
    showXAxisTooltip = true,
    decimals = 1,
    noDataMessage = "No Data Available",
    min = 0,
    max = 100,
    tickAmount = 10,
    isLoading = false,
    showZeroForNull = false,
    treatNullAsZero = false,
    showZeroLine = false,
    yAxisDecimals = 0,
    xAxisLabelFontSize = '15px',
    yAxisLabelFontSize = '15px',
    showDataLabels = false,
    dataLabelSuffix = '%',
    dataLabelsOffsetY = -10,
    dataLabelsOffsetYPerSeries,
    dataLabelsEnabledPerSeries,
    curve = 'straight',
    legendOffsetY = 0,
    legendItemMargin = DEFAULT_LEGEND_ITEM_MARGIN,
    showHorizontalGridLines = true,
    gridPadding = DEFAULT_GRID_PADDING,
    useCustomLegend = false,
    legendFontSize = '16px',
    legendFontWeight = 400,
    legendGap = '8px',
    legendMarkerSize = '10px',
    showInfoIcon = true,
    chartTitle,
    chartSubtitle,
    pageTitle,
    includeLegendInExport = showLegend,
}, ref) => {
    const chartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<ApexCharts | null>(null)

    const useReactLegend = showLegend && !useCustomLegend && showInfoIcon && series.length > 1

    const seriesKey = JSON.stringify(series)
    const stableSeries = useMemo(() => series, [seriesKey])
    const categoriesKey = JSON.stringify(categories)
    const stableCategories = useMemo(() => categories, [categoriesKey])

    const stripInvalidSvgAttrs = (el: Element) => {
        const attrsToRemove: string[] = []
        for (let i = 0; i < el.attributes.length; i++) {
            const attr = el.attributes[i]
            if (attr.name.includes(':') &&
                !attr.name.startsWith('xmlns') &&
                !attr.name.startsWith('xml:') &&
                !attr.name.startsWith('xlink:')) {
                attrsToRemove.push(attr.name)
            }
        }
        attrsToRemove.forEach(name => {
            try { el.removeAttribute(name) } catch (_) { }
        })
        for (let i = 0; i < el.children.length; i++) {
            stripInvalidSvgAttrs(el.children[i])
        }
    }

    const buildLegendSvgItems = (width: number, startY: number) => {
        const shouldIncludeLegend = showLegend || includeLegendInExport
        if (!chartRef.current || !shouldIncludeLegend) return { elements: [] as SVGElement[], height: 0 }

        let items: { color: string; text: string }[] = []
        const legendContainer = chartRef.current?.querySelector('.apexcharts-legend') as HTMLElement | null
        const legendItems = legendContainer?.querySelectorAll('.apexcharts-legend-series')
        if (legendItems?.length) {
            legendItems.forEach((item, index) => {
                const textEl = item.querySelector('.apexcharts-legend-text') as HTMLElement | null
                if (textEl) {
                    const color = colors[index % colors.length]
                    items.push({ color, text: textEl.textContent || '' })
                }
            })
        }
        if (items.length === 0 && includeLegendInExport && stableSeries.length > 0) {
            items = stableSeries.map((s, i) => ({
                color: colors[i % colors.length],
                text: s.name || ''
            }))
        }

        if (items.length === 0) return { elements: [] as SVGElement[], height: 0 }

        const elements: SVGElement[] = []
        const markerSize = 10
        const itemGap = 20
        const fontSize = 13

        let totalWidth = 0
        items.forEach((item, i) => {
            const textWidth = item.text.length * (fontSize * 0.6)
            totalWidth += markerSize + 6 + textWidth + (i < items.length - 1 ? itemGap : 0)
        })

        let xOffset = (width - totalWidth) / 2
        const y = startY + fontSize

        items.forEach((item) => {
            const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
            circle.setAttribute('cx', String(xOffset + markerSize / 2))
            circle.setAttribute('cy', String(y - fontSize / 3))
            circle.setAttribute('r', String(markerSize / 2))
            circle.setAttribute('fill', item.color)
            elements.push(circle)
            xOffset += markerSize + 6

            const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text')
            textEl.setAttribute('x', String(xOffset))
            textEl.setAttribute('y', String(y))
            textEl.setAttribute('font-size', String(fontSize))
            textEl.setAttribute('font-family', fontFamily)
            textEl.setAttribute('fill', '#333333')
            textEl.textContent = item.text
            elements.push(textEl)
            xOffset += item.text.length * (fontSize * 0.6) + itemGap
        })

        return { elements, height: fontSize + 12 }
    }

    const buildExportSvg = () => {
        if (!chartRef.current) return null
        const svgEl = chartRef.current.querySelector('.apexcharts-svg') as SVGSVGElement | null
        if (!svgEl) return null

        const clonedSvg = svgEl.cloneNode(true) as SVGSVGElement
        stripInvalidSvgAttrs(clonedSvg)

        const width = parseFloat(clonedSvg.getAttribute('width') || '800')
        const origHeight = parseFloat(clonedSvg.getAttribute('height') || '450')

        const headerFontForMeasure = '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        const wrapText = (text: string, fontSize: number, fontWeight: string, maxWidth: number): string[] => {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            if (!ctx) return [text]
            ctx.font = `${fontWeight} ${fontSize}px ${headerFontForMeasure}`
            const words = text.split(' ')
            const wrappedLines: string[] = []
            let currentLine = ''
            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word
                if (ctx.measureText(testLine).width > maxWidth && currentLine) {
                    wrappedLines.push(currentLine)
                    currentLine = word
                } else {
                    currentLine = testLine
                }
            }
            if (currentLine) wrappedLines.push(currentLine)
            return wrappedLines
        }

        let headerHeight = 10
        const lines: { text: string; fontSize: string; fontWeight: string; y: number }[] = []
        const textMaxWidth = width - 60

        if (pageTitle) {
            const wrappedPageTitle = wrapText(pageTitle, 20, 'bold', textMaxWidth)
            wrappedPageTitle.forEach(line => {
                headerHeight += 26
                lines.push({ text: line, fontSize: '20', fontWeight: 'bold', y: headerHeight })
            })
        }
        if (chartTitle) {
            const wrappedChartTitle = wrapText(chartTitle, 16, '600', textMaxWidth)
            wrappedChartTitle.forEach(line => {
                headerHeight += 22
                lines.push({ text: line, fontSize: '16', fontWeight: '600', y: headerHeight })
            })
        }
        if (chartSubtitle) {
            const wrappedSubtitle = wrapText(chartSubtitle, 14, '600', textMaxWidth)
            wrappedSubtitle.forEach(line => {
                headerHeight += 20
                lines.push({ text: line, fontSize: '14', fontWeight: '600', y: headerHeight })
            })
        }
        headerHeight += 15

        const legendResult = buildLegendSvgItems(width, headerHeight)
        headerHeight += legendResult.height

        const newHeight = origHeight + headerHeight

        const wrapperSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        wrapperSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
        wrapperSvg.setAttribute('width', String(width))
        wrapperSvg.setAttribute('height', String(newHeight))
        wrapperSvg.setAttribute('viewBox', `0 0 ${width} ${newHeight}`)

        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        bg.setAttribute('width', String(width))
        bg.setAttribute('height', String(newHeight))
        bg.setAttribute('fill', '#ffffff')
        wrapperSvg.appendChild(bg)

        const headerFontFamily = '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        lines.forEach(line => {
            const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text')
            textEl.setAttribute('x', String(width / 2))
            textEl.setAttribute('y', String(line.y))
            textEl.setAttribute('text-anchor', 'middle')
            textEl.setAttribute('font-size', line.fontSize)
            textEl.setAttribute('font-weight', line.fontWeight)
            textEl.setAttribute('font-family', headerFontFamily)
            textEl.setAttribute('fill', '#000000')
            textEl.textContent = line.text
            wrapperSvg.appendChild(textEl)
        })

        legendResult.elements.forEach(el => wrapperSvg.appendChild(el))

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        g.setAttribute('transform', `translate(0, ${headerHeight})`)
        while (clonedSvg.childNodes.length > 0) {
            g.appendChild(clonedSvg.childNodes[0])
        }
        wrapperSvg.appendChild(g)

        return { svg: wrapperSvg, width, height: newHeight }
    }

    const hasData = useMemo(() => {
        return stableSeries.length > 0 && stableSeries.some(s =>
            s.data && s.data.length > 0 && s.data.some(d => d !== null && d !== undefined)
        );
    }, [stableSeries]);

    const chartAnnotations = useMemo(() => {
        if (annotations) return annotations;

        const yaxisAnnotations: any[] = [];

        if (showZeroLine) {
            yaxisAnnotations.push({
                y: 0,
                borderColor: '#808080',
                borderWidth: 2,
                strokeDashArray: 0,
                label: {
                    show: false
                }
            });
        }

        if (goalLine) {
            yaxisAnnotations.push({
                y: goalLine,
                borderColor: '#009E73',
                borderWidth: 3,
                strokeDashArray: 1.25,
                label: {
                    borderColor: '#009E73',
                    offsetX: goalLabelOffsetX,
                    style: {
                        background: '#009E73',
                        color: '#fff',
                        fontSize: '14px',
                        fontFamily: fontFamily,
                    },
                    text: goalLabel,
                },
            });
        }

        return yaxisAnnotations.length > 0 ? { yaxis: yaxisAnnotations } : {};
    }, [annotations, goalLine, goalLabel, goalLabelOffsetX, fontFamily, showZeroLine]);

    const displaySeries = useMemo(() => {
        const base = !treatNullAsZero
            ? stableSeries
            : stableSeries.map(s => ({
                ...s,
                data: s.data.map(d => d === null || d === undefined ? 0 : d)
            }));
        if ((!dataLabelsOffsetYPerSeries && !dataLabelsEnabledPerSeries) || !showDataLabels) return base;
        return base.map((s, i) => ({
            ...s,
            dataLabels: {
                ...(dataLabelsOffsetYPerSeries ? { offsetY: dataLabelsOffsetYPerSeries[i] ?? dataLabelsOffsetY } : {}),
                ...(dataLabelsEnabledPerSeries ? { enabled: dataLabelsEnabledPerSeries[i] ?? true } : {}),
            }
        }));
    }, [stableSeries, treatNullAsZero, dataLabelsOffsetYPerSeries, dataLabelsEnabledPerSeries, showDataLabels, dataLabelsOffsetY]);

    const discreteMarkers = useMemo(() => {
        if (markers) return []
        const out: any[] = []
        displaySeries.forEach((s: any, seriesIndex: number) => {
            const data = s.data || []
            data.forEach((val: any, dataPointIndex: number) => {
                if (val === null || val === undefined) return
                const prev = dataPointIndex > 0 ? data[dataPointIndex - 1] : null
                const next = dataPointIndex < data.length - 1 ? data[dataPointIndex + 1] : null
                const prevIsNull = prev === null || prev === undefined
                const nextIsNull = next === null || next === undefined
                if (prevIsNull && nextIsNull) {
                    out.push({
                        seriesIndex,
                        dataPointIndex,
                        size: 5,
                        fillColor: colors[seriesIndex] || '#0072B2',
                        strokeColor: '#fff',
                    })
                }
            })
        })
        return out
    }, [displaySeries, markers, colors]);

    React.useImperativeHandle(ref, () => ({
        exportToCSV: () => {
            const header = ['Time Period', ...series.map(s => s.name)].join(',')
            const rows = categories.map((cat, i) => {
                const values = series.map(s => s.data[i] ?? '')
                return [cat, ...values].join(',')
            })
            const csvContent = [header, ...rows].join('\n')
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${exportFilename || 'chart'}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        },
        exportToSVG: () => {
            const result = buildExportSvg()
            if (result) {
                const svgString = new XMLSerializer().serializeToString(result.svg)
                const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `${exportFilename || 'chart'}.svg`
                link.click()
                URL.revokeObjectURL(url)
            } else {
                chartInstance.current?.exports.exportToSVG()
            }
        },
        exportToPNG: () => {
            const result = buildExportSvg()
            if (result) {
                    const svgString = new XMLSerializer().serializeToString(result.svg)
                const base64 = btoa(unescape(encodeURIComponent(svgString)))
                const dataUrl = `data:image/svg+xml;base64,${base64}`
                const img = new Image()
                img.onload = () => {
                    const scale = 2
                    const canvas = document.createElement('canvas')
                    canvas.width = result.width * scale
                    canvas.height = result.height * scale
                    const ctx = canvas.getContext('2d')
                    if (!ctx) return
                    ctx.scale(scale, scale)
                    ctx.fillStyle = '#ffffff'
                    ctx.fillRect(0, 0, result.width, result.height)
                    ctx.drawImage(img, 0, 0, result.width, result.height)
                    const link = document.createElement('a')
                    link.download = `${exportFilename || 'chart'}.png`
                    link.href = canvas.toDataURL('image/png')
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                }
                img.onerror = () => {
                    chartInstance.current?.exports.exportToPng()
                }
                img.src = dataUrl
            } else {
                chartInstance.current?.exports.exportToPng()
            }
        }
    }));

    useEffect(() => {
        if (!chartRef.current) return

        const options: ApexCharts.ApexOptions = {
            chart: {
                type: 'line',
                height,
                fontFamily: fontFamily,
                zoom: {
                    enabled: false,
                },
                toolbar: {
                    show: showToolbar,
                    tools: {
                        download: true,
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false,
                    },
                    export: exportFilename ? {
                        csv: { filename: exportFilename },
                        svg: { filename: exportFilename },
                        png: { filename: exportFilename }
                    } : undefined
                },
                animations: {
                    enabled: false,
                    dynamicAnimation: {
                        enabled: false
                    }
                }
            },
            series: displaySeries,
            noData: {
                text: '',
                align: 'center',
                verticalAlign: 'middle',
                offsetX: 0,
                offsetY: -45,
                style: {
                    color: 'Black',
                    fontSize: '12px',
                    fontFamily: fontFamily,
                }
            },
            xaxis: {
                categories: stableCategories,
                tooltip: {
                    enabled: showXAxisTooltip
                },
                title: {
                    text: xAxisLabel,
                    offsetY: 10,
                    style: {
                        fontSize: '15px',
                        color: 'Black',
                        fontWeight: 'normal',
                        fontFamily: fontFamily,
                    },
                },
                labels: {
                    style: {
                        fontSize: xAxisLabelFontSize,
                        colors: 'Black',
                        fontWeight: 'normal',
                        fontFamily: fontFamily,
                    },
                },
            },     
            yaxis: {
                min,
                ...(max !== undefined && max !== null && { max }),
                ...(tickAmount && max !== undefined && max !== null && { tickAmount: Math.round((max - min) / tickAmount) }),
                labels: {
                    formatter: (val) => {
                        if (yAxisDecimals > 0) {
                            const fixed = val.toFixed(yAxisDecimals);
                            return val % 1 === 0 ? Math.round(val).toString() : fixed;
                        }
                        return Math.round(val).toString();
                    },
                    style: {
                        fontSize: yAxisLabelFontSize,
                        colors: 'Black',
                        fontWeight: 'normal',
                        fontFamily: fontFamily,
                    },
                },
                title: {
                    text: yAxisLabel,
                    style: {
                        fontSize: '15px',
                        color: 'Black',
                        fontWeight: 'normal',
                        fontFamily: fontFamily,
                    },
                },
            },
            colors: colors,
            markers: {
                size: markers ? 5 : 0,
                discrete: discreteMarkers,
                strokeWidth: 2,
                strokeColors: '#fff',
                hover: {
                    size: 7,
                },
            },
            stroke: {
                width: 4,
                curve: curve,
            },
            grid: {
                padding: gridPadding,
                yaxis: {
                    lines: {
                        show: showHorizontalGridLines
                    }
                }
            },
            tooltip: {
                enabled: hasData,
                ...(customTooltip ? {
                    custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
                        const rawValue = series?.[seriesIndex]?.[dataPointIndex]
                        const value = rawValue === null || rawValue === undefined || Number.isNaN(rawValue) ? null : Number(rawValue)
                        const seriesName = w?.globals?.seriesNames?.[seriesIndex] || ''
                        const category = w?.globals?.categoryLabels?.[dataPointIndex] || stableCategories[dataPointIndex] || ''

                        return customTooltip({
                            seriesIndex,
                            dataPointIndex,
                            value,
                            seriesName,
                            category,
                        })
                    }
                } : {}),
                y: {
                    formatter: (val) => {
                        if (val == null) return showZeroForNull ? '0' : 'No Data';
                        if (decimals === 0) return `${Math.round(val)}`;
                        const fixed = val.toFixed(decimals);
                        return val % 1 === 0 ? Math.round(val).toString() : fixed;
                    },
                },
                style: {
                    fontFamily: fontFamily,
                }
            },
            legend: {
                show: showLegend && !useCustomLegend && !useReactLegend,
                position: legendPosition,
                horizontalAlign: legendAlign,
                fontFamily: fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                labels: {
                    colors: '#373D3F',
                },
                itemMargin: legendItemMargin,
                offsetY: legendOffsetY,
            },     
            responsive: [],
            annotations: chartAnnotations,
            dataLabels: {
                enabled: showDataLabels,
                formatter: function (val: string | number | number[], opts?: any): string | number {
                    if (dataLabelsEnabledPerSeries && opts?.seriesIndex !== undefined) {
                        if (dataLabelsEnabledPerSeries[opts.seriesIndex] === false) return '';
                    }
                    if (val === null || val === undefined) return '';
                    const numVal = typeof val === 'number' ? val : (Array.isArray(val) ? val[0] : parseFloat(val));
                    if (isNaN(numVal)) return '';
                    if (decimals === 0) return `${Math.round(numVal)}${dataLabelSuffix}`;
                    return numVal.toFixed(decimals);
                },
                offsetY: dataLabelsOffsetY,
                style: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    colors: ['#333'],
                    fontFamily: fontFamily,
                },
                background: {
                    enabled: false
                }
            },
        }

        if (chartInstance.current) {
            chartInstance.current.clearAnnotations();
            chartInstance.current.updateOptions(options, false, true);
            if (chartAnnotations?.yaxis) {
                setTimeout(() => {
                    if (chartInstance.current) {
                        chartInstance.current.clearAnnotations();
                        chartAnnotations.yaxis.forEach((annotation: any) => {
                            chartInstance.current?.addYaxisAnnotation(annotation, false);
                        });
                    }
                }, 50);
            }
        } else {
            chartInstance.current = new ApexCharts(chartRef.current, options)
            chartInstance.current.render()
        }
    }, [
        hasData,
        displaySeries,
        stableCategories,
        goalLine,
        height,
        yAxisLabel,
        xAxisLabel,
        colors,
        fontFamily,
        showToolbar,
        legendPosition,
        legendAlign,
        showLegend,
        exportFilename,
        chartAnnotations,
        markers,
        discreteMarkers,
        noDataMessage,
        min,
        max,
        tickAmount,
        decimals,
        showXAxisTooltip,
        xAxisLabelFontSize,
        legendOffsetY,
        legendItemMargin,
        showHorizontalGridLines,
        gridPadding
    ])

    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy()
                chartInstance.current = null
            }
        }
    }, [])

    const customLegendProps: CustomLegendProps = {
        showLegend,
        series: stableSeries,
        colors,
        legendGap,
        legendFontSize,
        legendFontWeight,
        legendMarkerSize,
        legendOffsetY,
        fontFamily,
        height,
        chartInstance,
        showInfoIcon,
    }

    return (
        <div className="line-chart-outer-container" style={{
            display: 'flex',
            flexDirection: legendPosition === 'right' ? 'row' : (legendPosition === 'left' ? 'row-reverse' : 'column'),
            alignItems: legendAlign === 'center' ? 'center' : 'flex-start',
            width: '100%'
        }}>
            {useReactLegend && (legendPosition === 'top') && (
                <div style={{ width: '100%', position: 'relative', zIndex: 3 }}>
                    <CustomLegend {...customLegendProps} layout="horizontal" horizontalAlign="center" />
                </div>
            )}
            <div className="line-chart-container" style={{ position: 'relative', flex: 1, width: '100%' }}>
                <div ref={chartRef} className="line-chart" style={{
                    opacity: isLoading ? 0.35 : 1,
                    transition: 'opacity 0.3s ease',
                    animation: isLoading ? 'chartPulse 1.5s ease-in-out infinite' : 'none',
                }} />
                {isLoading && (
                    <>
                        <style>{`
                            @keyframes chartPulse {
                                0%, 100% { opacity: 0.35; }
                                50% { opacity: 0.55; }
                            }
                            @keyframes chartSpinnerSpin {
                                0% { transform: translate(-50%, -50%) rotate(0deg); }
                                100% { transform: translate(-50%, -50%) rotate(360deg); }
                            }
                        `}</style>
                        <div style={{
                            position: 'absolute',
                            top: '45%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '32px',
                            height: '32px',
                            border: '3px solid #e0e0e0',
                            borderTop: '3px solid #004b8d',
                            borderRadius: '50%',
                            animation: 'chartSpinnerSpin 0.8s linear infinite',
                            zIndex: 10,
                        }} />
                    </>
                )}
                {!isLoading && !hasData && noDataMessage && (
                    <div style={{
                        position: 'absolute',
                        top: '40%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'Black',
                        fontSize: '12px',
                        fontFamily: fontFamily,
                        pointerEvents: 'none',
                        zIndex: 10,
                        textAlign: 'center'
                    }}>
                        {noDataMessage}
                    </div>
                )}
            </div>
            {useReactLegend && (legendPosition === 'bottom') && (
                <div style={{ width: '100%', position: 'relative', zIndex: 50 }}>
                    <CustomLegend {...customLegendProps} layout="horizontal" horizontalAlign="center" />
                </div>
            )}
            {useReactLegend && (legendPosition === 'left' || legendPosition === 'right') && (
                <CustomLegend {...customLegendProps} layout="vertical" />
            )}
            {useCustomLegend && (legendPosition === 'left' || legendPosition === 'right') && (
                <CustomLegend {...customLegendProps} />
            )}
            {useCustomLegend && (legendPosition === 'top' || legendPosition === 'bottom') && (
                <div style={{ width: '100%', display: 'flex', justifyContent: legendAlign }}>
                    <CustomLegend {...customLegendProps} />
                </div>
            )}
        </div>
    )
})

export default LineChart